import type { Product } from "../types";
import { COMMON_SPEC_FIELDS, SUBCATEGORY_SPEC_FIELDS } from "../data/specFields";

export interface ProductGroup {
  key: string;
  products: Product[];
  cheapest: Product;
  minPrice?: number;
  hasAnyPrice: boolean;
  variantCount: number;
}

export interface VariantAxisOption {
  /** Raw value — used for matching a product to a selection. */
  value: string;
  /** Prettified value — used for display (e.g. common prefix stripped). */
  label: string;
  /** The variant's own product photo, shown as a swatch for a color axis. */
  image?: string;
}

export interface VariantAxis {
  key: string;
  label: string;
  options: VariantAxisOption[];
}

// Groups a flat, already-filtered/sorted product list by exact `name` — every
// product ends up in exactly one group (size >= 1), so callers never need to
// special-case "standalone vs grouped." Map iteration preserves the input
// order, so the group order follows each group's first-seen position,
// respecting whatever sort was applied upstream (price, recommended, etc.).
//
// Electrolux Professional is excluded from name-based grouping: its
// catalog depth (400+ capacity/door/refrigerant/electrical variants
// sharing one model name) made the resulting variant selector unusably
// large, so each Electrolux SKU is kept as its own single-product
// card/page instead.
//
// Raynaud went through two revisions: first grouped by name like every
// other brand, which surfaced a raw-model-number "Конфигурация" picker
// (no characteristics existed yet) — fixed by adding a size_cm
// characteristic. Then excluded from grouping entirely, standalone-only,
// after a name-based group briefly pulled two differently-decorated
// pieces together (before color was baked into the name itself, so two
// distinct Chinese-zodiac-motif Uni bowls could share a near-identical
// cleaned name). Now that every color/motif variant carries its own
// distinct name (see build_final2.py's `full_name`), grouping by name is
// safe again: a color mismatch can no longer collapse into one group,
// since the color text itself is part of what's being matched. Re-enabled
// per explicit user request, specifically so same-color/different-size
// rows (e.g. Chelsea's "Тарелка плоская с бортом ... — Золото фон Белый"
// across several sizes) unite into one card with a size picker instead of
// a separate standalone card per size.
// Drops a group member that adds no selectable distinction over an
// identically-priced sibling — e.g. a pre-split seed entry whose id was
// renamed when it got split into real per-config variants, but whose row
// lingers in the DB because the sync loop only creates/updates, never
// deletes (see the stale-id cleanup blocks in server/db.ts). If every
// characteristics key `a` has is also on `b` with the same value, and `b`
// has at least one key `a` lacks, `a` is a strict information subset of
// `b` at the same price — showing both as separate variant options would
// render a redundant/blank option for `a`'s missing axis value, so `a` is
// dropped in favor of the more complete `b`.
function dropRedundantMembers(members: Product[]): Product[] {
  return members.filter((a) => {
    const aChar = a.characteristics ?? {};
    const aKeys = Object.keys(aChar);
    return !members.some((b) => {
      if (a === b || a.price !== b.price) return false;
      const bChar = b.characteristics ?? {};
      const aIsSubsetOfB = aKeys.every((k) => bChar[k] === aChar[k]);
      return aIsSubsetOfB && Object.keys(bChar).length > aKeys.length;
    });
  });
}

// Raynaud's Shanghai deep plate (21cm/22.5cm, ids raynaud-shanghai-250021/
// -250022) is a customer-requested exception to the general name-based
// grouping below — every other same-name size variant should still unite
// into one card with a size picker, but the customer specifically wants
// this one pair kept as two separate catalog cards.
const UNGROUPED_PRODUCT_IDS = new Set(["raynaud-shanghai-250021", "raynaud-shanghai-250022"]);

export function buildProductGroups(products: Product[]): ProductGroup[] {
  const byName = new Map<string, Product[]>();
  for (const p of products) {
    const key =
      p.brand === "Electrolux Professional" || UNGROUPED_PRODUCT_IDS.has(p.id)
        ? `id:${p.id}`
        : p.name.trim();
    const existing = byName.get(key);
    if (existing) existing.push(p);
    else byName.set(key, [p]);
  }

  const groups: ProductGroup[] = [];
  for (const [key, rawMembers] of byName) {
    const members = dropRedundantMembers(rawMembers);
    const sorted = [...members].sort((a, b) => {
      if (a.price === undefined && b.price === undefined) return 0;
      if (a.price === undefined) return 1;
      if (b.price === undefined) return -1;
      return a.price - b.price;
    });
    const withPrice = sorted.filter((p) => p.price !== undefined);
    groups.push({
      key,
      products: sorted,
      cheapest: sorted[0],
      minPrice: withPrice[0]?.price,
      hasAnyPrice: withPrice.length > 0,
      variantCount: sorted.length,
    });
  }
  return groups;
}

function stringifyCharValue(v: string | number | undefined): string {
  return v === undefined ? "" : String(v);
}

function charLabel(product: Product, key: string): string {
  const defs = [
    ...COMMON_SPEC_FIELDS,
    ...(product.subcategory ? SUBCATEGORY_SPEC_FIELDS[product.subcategory] ?? [] : []),
  ];
  // A raw characteristics key (e.g. "capacity_kg") is a data-model detail,
  // not customer-facing copy — every real axis should have a translated
  // label in specFields.ts. If one's missing, "Опция" (matching
  // guessSegmentLabel's fallback above) is safer than leaking the key.
  return defs.find((d) => d.key === key)?.label ?? "Опция";
}

function modelSegments(model: string): string[] {
  return model.split(" - ").map((s) => s.trim());
}

// Strips the words every value shares as a leading prefix — turns
// ["SKYLINE PROS OVEN 6 GN 1/1", "SKYLINE PROS OVEN 10 GN 1/1"] into
// ["6 GN 1/1", "10 GN 1/1"] for display, while the raw value (used for
// matching) stays untouched.
function stripCommonPrefix(values: string[]): string[] {
  if (values.length <= 1) return values;
  const wordLists = values.map((v) => v.split(" "));
  const minLen = Math.min(...wordLists.map((w) => w.length));
  let prefixLen = 0;
  outer: for (let i = 0; i < minLen; i++) {
    const word = wordLists[0][i];
    for (const w of wordLists) if (w[i] !== word) break outer;
    prefixLen++;
  }
  if (prefixLen === 0) return values;
  return wordLists.map((w) => {
    const rest = w.slice(prefixLen).join(" ");
    return rest || w.join(" ");
  });
}

// Heuristic label for a model-segment axis, based on the vocabulary actually
// seen in its options — keeps this generic instead of hand-coding per brand.
// If a segment's values match more than one category (e.g. it mixes an IoT
// toggle with a door-hinge side and an unrelated sub-line tag), that's a
// signal the segment isn't a single coherent attribute — falling back to a
// generic label is more honest than confidently mislabeling it.
function guessSegmentLabel(rawValues: string[]): string {
  const upper = rawValues.map((v) => v.toUpperCase());
  const matches = {
    "Ёмкость": upper.some((v) => /\bGN\b/.test(v)),
    "Тип питания": upper.every((v) => v === "" || /ELECTRIC|GAS|ГАЗ|ЭЛЕКТР/.test(v)),
    "Стекло двери": upper.some((v) => /GLASS|СТЕКЛ/.test(v)),
    "Модуль": upper.some((v) => /IOT/.test(v)),
    "Открывание двери": upper.some((v) => /HINGED|DOOR|ДВЕР/.test(v)),
  };
  const matchedLabels = Object.entries(matches)
    .filter(([, isMatch]) => isMatch)
    .map(([label]) => label);
  return matchedLabels.length === 1 ? matchedLabels[0] : "Опция";
}

function axisValueForProduct(product: Product, axisKey: string): string {
  if (axisKey.startsWith("char:")) {
    return stringifyCharValue(product.characteristics?.[axisKey.slice(5)]);
  }
  if (axisKey === "model:full") return product.model;
  if (axisKey.startsWith("model:")) {
    const idx = Number(axisKey.slice(6));
    return modelSegments(product.model)[idx] ?? "";
  }
  return "";
}

// Extracts the selectable dimensions for a group — generic, not brand-specific:
// 1. Any `characteristics` key whose value differs across members (covers
//    Nilma-style groups, where the varying spec lives in one clean key).
// 2. Any position in the `" - "`-delimited `model` string that differs
//    across members (recovers Electrolux's capacity/power/glass, which
//    `characteristics` doesn't carry).
// 3. A "Конфигурация" fallback listing full model strings, if nothing else
//    varies — always renders something, never crashes.
// Axes whose option assignment is identical to one already found (e.g. a
// model-segment axis that duplicates a cleaner characteristics axis) are
// dropped, keeping the characteristics-based one.
export function extractVariantAxes(group: ProductGroup): VariantAxis[] {
  const { products } = group;
  if (products.length <= 1) return [];

  type Candidate = { key: string; label: string; rawOptions: string[] };
  const candidates: Candidate[] = [];

  const allCharKeys = new Set<string>();
  for (const p of products) {
    for (const k of Object.keys(p.characteristics ?? {})) allCharKeys.add(k);
  }
  for (const k of allCharKeys) {
    const values = products.map((p) => stringifyCharValue(p.characteristics?.[k]));
    if (new Set(values).size > 1) {
      candidates.push({ key: `char:${k}`, label: charLabel(products[0], k), rawOptions: values });
    }
  }

  const segLists = products.map((p) => modelSegments(p.model));
  const maxSegs = Math.max(...segLists.map((s) => s.length));
  if (maxSegs > 1) {
    for (let i = 0; i < maxSegs; i++) {
      const values = segLists.map((s) => s[i] ?? "");
      if (new Set(values).size > 1) {
        candidates.push({ key: `model:${i}`, label: guessSegmentLabel(values), rawOptions: values });
      }
    }
  }

  const hadRealCandidates = candidates.length > 0;
  if (!hadRealCandidates) {
    candidates.push({
      key: "model:full",
      label: "Конфигурация",
      rawOptions: products.map((p) => p.model),
    });
  }

  const buildAxes = (list: Candidate[]): VariantAxis[] => {
    // Drop axes whose option assignment exactly duplicates an earlier one.
    const seenSignatures = new Set<string>();
    const built: VariantAxis[] = [];
    for (const c of list) {
      const signature = c.rawOptions.join("|");
      if (seenSignatures.has(signature)) continue;
      seenSignatures.add(signature);

      const prettified = stripCommonPrefix(c.rawOptions);
      const seenValues = new Set<string>();
      const options: VariantAxisOption[] = [];
      c.rawOptions.forEach((raw, i) => {
        // A blank raw value means this product has no value at all for the
        // axis (e.g. a sibling that predates a `characteristics` key added
        // to newer members) — rendering it as a button would show an empty
        // "—" placeholder with no real option name, so it's skipped rather
        // than displayed.
        if (raw === "") return;
        if (seenValues.has(raw)) return;
        seenValues.add(raw);
        // A color axis, or a raw-model "Конфигурация" fallback (every option
        // is a distinct model, so each one safely maps to its own product —
        // see the distinguishesEveryProduct comment below), shows each
        // option's own photo as a swatch — the real photo is honest about
        // the actual finish, where a text label would require asserting a
        // color name this project can't always verify (see the
        // TABLEWARE_GLAZE_COLORS mislabeling note in specFields.ts).
        // A color axis prefers a dedicated swatch photo when the product
        // supplies one — its own hero/gallery image may deliberately show
        // the full item (e.g. an admin picked a real product photo over an
        // abstract rim-color close-up), which would otherwise make every
        // color option render the same picture.
        const image =
          c.key === "char:color"
            ? products[i]?.swatchImage ?? products[i]?.image
            : c.key === "model:full"
              ? products[i]?.image
              : undefined;
        options.push({ value: raw, label: prettified[i] || raw, image });
      });

      built.push({ key: c.key, label: c.label, options });
    }
    return built;
  };

  // A candidate survives the `new Set(values).size > 1` check above using
  // its raw values, blanks included (e.g. ["30/36 vol.", ""] when only one
  // sibling has this characteristic at all) — but buildAxes then drops
  // blanks entirely, so what looked like a 2-value axis can resolve to a
  // single real button. That's not a choice, it's a spurious "picker" —
  // and worse, cartesianSizeMatchesProductCount multiplies by each axis's
  // *post-filter* options.length, so a 1-option axis contributes a factor
  // of 1 and is invisible to the very check meant to catch axes that don't
  // actually distinguish every product. Dropping degenerate axes here,
  // before that check runs, fixes both: no dead picker renders, and the
  // safety math only ever sees axes that could possibly matter.
  let axes = buildAxes(candidates).filter((axis) => axis.options.length >= 2);

  // The found axes only work if they actually pin down a single product per
  // combination. Some catalogues (e.g. Tognana's "Factory" glaze families)
  // vary by an undocumented decorative finish that isn't captured in
  // `characteristics` at all — the only axis extractVariantAxes could find
  // (e.g. size) is shared by several differently-photographed products, so
  // picking "size 20" silently returns whichever one sorts first and hides
  // the rest, even though their distinct photos all appear in the card's
  // hover-cycle preview. Model codes are globally unique (verified via the
  // catalog's own dedup step), so falling back to the raw model always
  // resolves this — and since every model differs here, each option gets to
  // show its own product photo as a swatch instead of a naked SKU-code
  // button, the same honest-photo treatment as a real color axis.
  const distinguishesEveryProduct = (candidateAxes: VariantAxis[]): boolean => {
    if (candidateAxes.length === 0) return products.length <= 1;
    const seen = new Set<string>();
    for (const p of products) {
      const tuple = candidateAxes.map((a) => axisValueForProduct(p, a.key)).join(" ");
      if (seen.has(tuple)) return false;
      seen.add(tuple);
    }
    return true;
  };

  // Independent pickers are only safe when every combination of their
  // options corresponds to a real product. Uniqueness alone (above) isn't
  // enough: two axes can each distinguish every product yet still overcount
  // the real combination space whenever the group has fewer products than
  // the full cartesian size of the axes shown — e.g. a "power" axis with 2
  // options and a "weight" axis with 2 options implies 4 buyable
  // combinations, but if only 2 products actually exist (power and weight
  // move together as one real variant, not two independent ones), the
  // other 2 combinations are phantom SKUs a shopper could pick and get
  // nothing for. That gap is exactly the bug: showing weight as its own
  // picker lets you choose a power/weight pairing that was never made.
  const cartesianSizeMatchesProductCount = (candidateAxes: VariantAxis[]): boolean => {
    const size = candidateAxes.reduce((n, a) => n * a.options.length, 1);
    return size === products.length;
  };

  const isSafeAxisSet = (candidateAxes: VariantAxis[]): boolean =>
    distinguishesEveryProduct(candidateAxes) && cartesianSizeMatchesProductCount(candidateAxes);

  if (!isSafeAxisSet(axes)) {
    // The full combination of every varying characteristic overcounts the
    // real SKU space (see cartesianSizeMatchesProductCount above) — e.g.
    // power and weight vary together as one real variant, not two
    // independent ones. Rather than dropping straight to a bare model-code
    // picker, try each candidate characteristic alone first: a single one
    // (e.g. just power) often already distinguishes every product by
    // itself, and showing that one meaningful spec — with the other
    // co-varying characteristics implied by the match rather than exposed
    // as their own picker — is far more useful to a shopper than a SKU
    // selector. Candidates are tried in the order extractVariantAxes found
    // them (characteristics first, in each product's own key order, so
    // e.g. `power_w` before a later `weight_kg`), keeping the first one
    // that alone pins down a single product per option.
    const singleAxisFallback = candidates
      .map((c) => buildAxes([c])[0])
      .find((axis) => isSafeAxisSet([axis]));

    axes = singleAxisFallback
      ? [singleAxisFallback]
      : buildAxes([
          { key: "model:full", label: hadRealCandidates ? "Вариант" : "Конфигурация", rawOptions: products.map((p) => p.model) },
        ]);
  }

  // Give a bare model-code axis the same photo-swatch treatment as a real
  // color axis, but only when every option actually has its own distinct
  // photo — otherwise two option buttons would show the same picture with
  // no visual way to tell them apart, which is worse than a text label.
  for (const axis of axes) {
    if (axis.key !== "model:full") continue;
    const images = products.map((p) => p.image);
    const allDistinct = new Set(images).size === images.length;
    if (!allDistinct) continue;
    for (const option of axis.options) {
      const product = products.find((p) => p.model === option.value);
      option.image = product?.image;
    }
  }

  return axes;
}

export function getProductAxisValue(product: Product, axisKey: string): string {
  return axisValueForProduct(product, axisKey);
}

export function pluralizeConfigurations(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "конфигураций";
  if (mod10 === 1) return "конфигурация";
  if (mod10 >= 2 && mod10 <= 4) return "конфигурации";
  return "конфигураций";
}

export function findVariantMatch(
  group: ProductGroup,
  axes: VariantAxis[],
  selected: Record<string, string>
): Product | undefined {
  return group.products.find((p) =>
    axes.every((axis) => axisValueForProduct(p, axis.key) === selected[axis.key])
  );
}
